import React, { useEffect, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import commonStyles from "../../styles/Common.module.css";

const MONTH_ORDER = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const thStyle = {
    padding: "10px 12px",
    textAlign: "right",
    fontWeight: "bold",
    borderBottom: "2px solid #ddd",
    whiteSpace: "nowrap",
    backgroundColor: "#f5f5f5",
};

const thFirstStyle = {
    ...thStyle,
    textAlign: "left",
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: "#f5f5f5",
};

const tdStyle = {
    padding: "10px 12px",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #eee",
};

const tdFirstStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: "500",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: "#fff",
};

const RevenueTable = ({ title, monthCols, rows }) => {
    return (
        <div style={{ marginBottom: "50px" }}>
            <h2 style={{ marginBottom: "14px" }}>{title}</h2>
            {(!rows || rows.length === 0) ? (
                <p style={{ color: "#999", fontStyle: "italic" }}>No data available</p>
            ) :
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead>
                        <tr>
                            <th style={thFirstStyle}>Member</th>
                            {monthCols.map((m) => (
                                <th key={m} style={thStyle}>{m}</th>
                            ))}
                            <th style={thStyle}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ind) => {
                            const rowTotal = monthCols.reduce((sum, m) => sum + (row[m] || 0), 0);
                            return (
                                <tr key={ind}>
                                    <td style={tdFirstStyle}>{row.name}</td>
                                    {monthCols.map((m) => (
                                        <td key={m} style={tdStyle}>
                                            {row[m] ? `₹${row[m].toLocaleString("en-IN")}` : "-"}
                                        </td>
                                    ))}
                                    <td style={{ ...tdStyle, fontWeight: "bold" }}>
                                        ₹{rowTotal.toLocaleString("en-IN")}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            }
        </div>
    );
};

const ManualRevenue = () => {
    const [monthCols, setMonthCols] = useState([]);
    const [totalRows, setTotalRows] = useState([]);
    const [fullAccessRows, setFullAccessRows] = useState([]);
    const [timesRows, setTimesRows] = useState([]);
    const [mentorshipRows, setMentorshipRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch("https://api.sovrenn.com/manual/revenue-report", {
                    headers: {
                        "Authorization": "Bearer " + localStorage.getItem("sov_user")
                    }
                });
                const result = await res.json();

                if (result.success) {
                    transformData(result.data);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const transformData = (data) => {
        // Sort months chronologically
        const sorted = [...data].sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month);
        });

        // Column headers: "April 2025", "May 2025", ...
        const cols = sorted.map((d) => `${d.month} ${d.year}`);

        // All unique members across all months
        const allMembers = [...new Set(sorted.flatMap((d) => d.members.map((m) => m.given_by)))];

        // Build lookup: memberName -> "Month Year" -> { total, full_access, news, course }
        const FULL_ACCESS_TYPES = ["full-access", "life", "monthly", "quarterly"];

        const lookup = {};
        sorted.forEach(({ month, year, members }) => {
            const key = `${month} ${year}`;
            members.forEach(({ given_by, total_revenue, type_breakdown }) => {
                if (!lookup[given_by]) lookup[given_by] = {};
                lookup[given_by][key] = {
                    total: total_revenue || 0,
                    full_access: type_breakdown
                        .filter((t) => FULL_ACCESS_TYPES.includes(t.type))
                        .reduce((sum, t) => sum + (t.revenue || 0), 0),
                    news: type_breakdown.find((t) => t.type === "news")?.revenue || 0,
                    course: type_breakdown
                        .filter((t) => t.type === "video-course")
                        .reduce((sum, t) => sum + (t.revenue || 0), 0),
                };
            });
        });

        const buildRows = (key) =>
            allMembers.map((name) => {
                const row = { name };
                cols.forEach((col) => {
                    row[col] = lookup[name]?.[col]?.[key] || 0;
                });
                return row;
            });

        setMonthCols(cols);
        setTotalRows(buildRows("total"));
        setFullAccessRows(buildRows("full_access"));
        setTimesRows(buildRows("news"));
        setMentorshipRows(buildRows("course"));
    };

    return (
        <div className={commonStyles.MainContainer}>
            <div className={commonStyles.headerDiv}>
                <h1>Sales Team Revenue</h1>
            </div>

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", margin: "100px auto", width: 100 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <div style={{ marginTop: "30px" }}>
                    <RevenueTable title="Total Revenue" monthCols={monthCols} rows={totalRows} />
                    <RevenueTable title="Full Access Revenue" monthCols={monthCols} rows={fullAccessRows} />
                    <RevenueTable title="Times Access Revenue" monthCols={monthCols} rows={timesRows} />
                    <RevenueTable title="Courses Revenue" monthCols={monthCols} rows={mentorshipRows} />
                </div>
            )}
        </div>
    );
};

export default ManualRevenue;
